import { useEffect, useMemo, useState } from 'react';
import { Col, Form, Row } from 'antd';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from 'components';
import { DeleteIcon, EditIcon } from 'icons';
import { EyeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import FacilityExtrastocksApi from 'libs/api/facility/extrastocksApi';
import { useApi } from 'hooks/useApi';
import ExtrastocksAdapter from './extrastocks.adapter';
import { useRecoilValue } from 'recoil';
import { getPage, getShoppingCenters } from 'hooks/useUser';
import UIHelper from 'helpers/UIHelper';
import ErrorHelper from 'helpers/ErrorHelper';

const api = new FacilityExtrastocksApi();

const useExtraStocks = (isMainPage = true, isEdit) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  let { id } = useParams();
  const navigate = useNavigate();
  const shoppingCenters = useRecoilValue(getShoppingCenters);
  const pages = useRecoilValue(getPage);
  const [form] = Form.useForm();
  const [units, setUnits] = useState([]);
  const {
    loading,
    data,
    onChangeTableFilter,
    handleDownloadExcel,
    onDelete,
    updateInProcess,
    singleItem,
    setUpdateInProcess,
  } = useApi({
    resourceKey: 'extrastocks',
    singleResourceKey: 'extrastock',
    api: api,
    isMainPage,
    adapter: ExtrastocksAdapter,
  });

  const extrastockType = useMemo(
    () => [
      { value: 'structural', title: t('Struttura') },
      { value: 'container', title: t('Container') },
    ],
    [t],
  );

  const extrastockTypeLabels = useMemo(
    () =>
      extrastockType.reduce((acc, { value, title }) => {
        acc[value] = title;
        return acc;
      }, {}),
    [extrastockType],
  );

  const filters = useMemo(
    () => [
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'by_shopping_center',
        values: shoppingCenters.map((i) => ({ value: i.id, title: i.title })),
      },
      {
        title: t('UNITA'),
        type: 'select',
        name: 'UNITÀ',
        isSearch: true,
        values: units.map((i) => ({
          value: i.title,
          title: `${i.title} (${i.shopping_center.title})`,
        })),
      },
      {
        title: t('Tipologia Stock'),
        type: 'select',
        name: 'by_extrastock_type',
        values: extrastockType,
      },
    ],
    [t, shoppingCenters, units],
  );

  const columns = useMemo(() => {
    return [
      {
        title: t('Extrastock'),
        dataIndex: 'title',
        fixed: 'left',
        sorter: () => { },
      },
      {
        title: t('MQ'),
        dataIndex: 'square',
        width: 100,
        sorter: () => { },
      },
      {
        title: t('Tipologia Stock'),
        dataIndex: 'extrastock_type',
        sorter: () => { },
        render: (data) => extrastockTypeLabels[data] || data,
      },
      {
        title: t('Outlet Village'),
        dataIndex: 'shopping_centers.title',
        sorter: () => { },
      },
      {
        title: t('Listino canone d’affitto (€/mese)'),
        dataIndex: 'rent_price',
        sorter: () => { },
      },
      {
        title: t('Listino utenze (€/mese)'),
        dataIndex: 'consumption_price',
        sorter: () => { },
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 160,
        fixed: 'right',
        render: (data, row) =>
          (pages?.ability?.can_read !== false ||
            row.can_read !== false ||
            row.can_update !== false ||
            row.can_destroy !== false) && (
            <Row
              gutter={16}
              justify={'end'}
              onClick={(e) => e.stopPropagation()}
            >
              {(pages?.ability?.can_read !== false ||
                row.can_read !== false) && (
                <Col>
                  <Link to={`${pathname}/${row.id}`}>
                    <Button type={'secondary'} icon={<EyeOutlined />} />
                  </Link>
                </Col>
              )}
              {row.can_update !== false && (
                <Col>
                  <Link to={`${pathname}/${row.id}/edit`}>
                    <Button type={'secondary'} icon={<EditIcon />} />
                  </Link>
                </Col>
              )}
              {row.can_destroy !== false && (
                <Col>
                  <Link to={`${pathname}/${row.id}/delete`}>
                    <Button type={'red'} icon={<DeleteIcon />} />
                  </Link>
                </Col>
              )}
            </Row>
          ),
      },
    ];
  }, [pathname, pages?.ability?.can_read, extrastockTypeLabels]);

  useEffect(() => {
    (async () => {
      try {
        const {
          data: { extrastocks = [] },
        } = await api.list({
          params: {
            per: 10000,
          },
        });
        setUnits(extrastocks);
      } catch (e) {
        console.log(e);
      }
    })();
  }, []);

  const handleCreateOrUpdate = async (values) => {
    setUpdateInProcess(true);
    try {
      const params = {
        data: {
          extrastock: {
            ...values,
          },
        },
      };
      if (!id) {
        await api.create(params);
        navigate(UIHelper.getListRoute(pathname, 1));
      } else {
        await api.put(id, params);
        navigate(UIHelper.getListRoute(pathname, 1) + '?success=true');
      }
    } catch (e) {
      const errors = e.response.data || [];
      form.setFields(
        ErrorHelper.getErrorsForForm(errors, {
          shopping_center: 'shopping_center_id',
        }),
      );
    } finally {
      setUpdateInProcess(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (id) {
        try {
          if (isEdit) {
            let fieldsValues = [];
            for (let key in singleItem) {
              if (key !== 'id' && key !== 'shopping_center') {
                fieldsValues.push({
                  name: key,
                  value: singleItem[key],
                });
              } else if (key === 'shopping_center') {
                fieldsValues.push({
                  name: 'shopping_center_id',
                  value: singleItem[key]?.id,
                });
              }
              form.setFields(fieldsValues);
            }
          }
        } catch (e) {
          console.log(e);
        }
      }
    })();
  }, [singleItem]);

  return {
    columns,
    data,
    loading,
    onChangeTableFilter,
    filters,
    form,
    shoppingCenters,
    extrastockType,
    updateInProcess,
    singleItem,
    handleDownloadExcel,
    handleCreateOrUpdate,
    onDelete,
  };
};

export default useExtraStocks;
